'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { DaemonTriggerDetailContent as SharedDaemonTriggerDetailContent } from '@agentteams/runner-request/ui/components/DaemonTriggerDetailContent';
import { AttachmentCountButton } from './AttachmentCountButton';
import { MarkdownViewer } from './MarkdownViewer';
import { TiptapViewer } from './TiptapViewer';
import { SavePromptAsPresetButton } from './PromptPresetSelector';
import { useWebRunnerRequestDetailAdapter } from '../hooks/useWebRunnerRequestDetailAdapter';
import { getSupportedRunnerTypesForDaemon } from '../hooks/useDaemonTriggerModal';
import { useDaemons, useSharedDaemons } from '../hooks/queries/useDaemons';

export type { DaemonTriggerDetailProps } from '@agentteams/runner-request/ui/components/DaemonTriggerDetailContent';
export type {
  DaemonTriggerDetailSlots,
  SharedDaemonTriggerDetailProps,
} from '@agentteams/runner-request/ui/components/DaemonTriggerDetailContent';

/**
 * 웹 슬롯(마크다운·프롬프트 뷰어·첨부·프리셋 저장)과 데이터 어댑터를 바인딩한 상세 본문.
 * 섹션·로그·액션 구성은 공용 패키지가 소유하고, 이 모듈은 기존 import 경로만 유지한다.
 * 이어쓰기 엔진 목록은 요청한 러너 기준으로 제한한다(요청 모달과 같은 규칙).
 */
export const DaemonTriggerDetailContent = (
  props: Omit<
    Parameters<typeof SharedDaemonTriggerDetailContent>[0],
    'detail' | 'renderMarkdown' | 'renderPromptViewer' | 'renderAttachmentAction' | 'renderSavePresetButton'
  >,
) => {
  const tRoot = useTranslations();
  const detail = useWebRunnerRequestDetailAdapter(tRoot);
  const trigger = props.trigger;
  // 이어쓰기는 같은 러너에서 실행되므로, 그 러너가 쓸 수 있는 엔진만 보여준다(요청 모달과 같은 규칙).
  const triggerDaemonId = trigger?.claimedByDaemonId ?? trigger?.targetDaemonId ?? null;
  const token = detail.session.token;
  const projectId = trigger?.projectId ?? detail.session.scope.projectId;
  const { data: daemonsResult } = useDaemons(token, { enabled: Boolean(triggerDaemonId) });
  const { data: sharedDaemons } = useSharedDaemons(token, projectId, {
    enabled: Boolean(triggerDaemonId),
  });
  const supportedContinueRunnerTypes = useMemo(() => {
    if (!triggerDaemonId) {
      return null;
    }
    const triggerDaemon =
      [...(daemonsResult?.daemons ?? []), ...(sharedDaemons ?? [])].find((d) => d.id === triggerDaemonId) ?? null;
    return getSupportedRunnerTypesForDaemon(triggerDaemon);
  }, [daemonsResult?.daemons, sharedDaemons, triggerDaemonId]);
  return (
    <SharedDaemonTriggerDetailContent
      {...props}
      detail={detail}
      supportedContinueRunnerTypes={props.supportedContinueRunnerTypes ?? supportedContinueRunnerTypes}
      renderMarkdown={(content) => <MarkdownViewer content={content} />}
      renderPromptViewer={(content) => <TiptapViewer content={content} minHeight={0} />}
      renderAttachmentAction={(triggerId) => (
        <AttachmentCountButton
          targetType="daemonTrigger"
          targetId={triggerId}
          className="text-[var(--color-fg-muted)] hover:text-base-content"
        />
      )}
      renderSavePresetButton={(input) => <SavePromptAsPresetButton {...input} />}
    />
  );
};
